import type { ILiveUpdateMessage } from "@/logic/api";

export type DirectLiveUpdateType = Exclude<ILiveUpdateMessage["type"], "live.summary">;
type LiveUpdateEventType = DirectLiveUpdateType | (string & {});

export interface LiveUpdateEvent {
  type: LiveUpdateEventType;
  payload: Readonly<Record<string, string>>;
  publishedAt: string;
  summaryCount?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isDirectEventType = (value: unknown): value is LiveUpdateEventType =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  value !== "live.summary";

const parsePayload = (
  value: unknown,
): Readonly<Record<string, string>> | null => {
  if (!isRecord(value)) return null;

  const entries = Object.entries(value);
  if (!entries.every(([, item]) => typeof item === "string")) return null;

  return Object.fromEntries(entries) as Readonly<Record<string, string>>;
};

const isTimestamp = (value: unknown): value is string =>
  typeof value === "string" &&
  value.trim().length > 0 &&
  !Number.isNaN(Date.parse(value));

const parseSummaryCount = (value: unknown): number | undefined | null => {
  if (value === undefined) return undefined;
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0
    ? value
    : null;
};

/** Validates untrusted transport or BroadcastChannel input; it never throws, the app just ignores the message. */
export const parseLiveUpdateMessage = (
  value: unknown,
): LiveUpdateEvent | null => {
  if (!isRecord(value) || !isTimestamp(value.publishedAt)) return null;

  const payload = parsePayload(value.payload);
  if (!payload) return null;

  if (isDirectEventType(value.type)) {
    const summaryCount = parseSummaryCount(value.summaryCount);
    if (summaryCount === null) return null;
    return {
      type: value.type,
      payload,
      publishedAt: value.publishedAt,
      ...(summaryCount === undefined ? {} : { summaryCount }),
    };
  }

  if (value.type !== "live.summary") return null;

  const count = Number(payload.count);
  if (
    !isDirectEventType(payload.of) ||
    !/^\d+$/.test(payload.count ?? "") ||
    !Number.isSafeInteger(count) ||
    count < 1
  ) {
    return null;
  }

  return {
    type: payload.of,
    // A summary identifies a changed event type, not any one changed entity;
    // Empty payload makes page rules invalidate broadly rather than trust ids
    // TODO: DD - This might change in the future to include more information about the changed entities.
    payload: {},
    publishedAt: value.publishedAt,
    summaryCount: count,
  };
};
